/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatDialogConfig, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MatBottomSheetConfig, MAT_BOTTOM_SHEET_DEFAULT_OPTIONS } from '@angular/material/bottom-sheet';
import { MatIconRegistry } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatTooltipDefaultOptions, MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { QRCodeComponent } from 'angularx-qrcode';
import { svgSprite } from '../assets/hig-icons';
import { NvHigBottomSheetComponent } from './components/nv-hig-bottom-sheet/nv-hig-bottom-sheet.component';
import { NvHigCarouselComponent } from './components/nv-hig-carousel/nv-hig-carousel.component';
import { NvHigCellComparisonComponent } from './components/nv-hig-cell-upgrade/nv-hig-cell_comparison.component';
import { NvHigDialogComponent } from './components/nv-hig-dialog/nv-hig-dialog.component';
import { NvHigFullPageDialogComponent } from './components/nv-hig-fullpage-dialog/nv-hig-fullpage-dialog.component';
import { NvHigTableComparisonComponent } from './components/nv-hig-generic-table/nv-hig-table_comparison.component';
import { NvHigListInsertComponent } from './components/nv-hig-list-insert/nv-hig-list-insert.component';
import { NvHigListItemContextCtaComponent } from './components/nv-hig-list-item/nv-hig-list-item-context-cta/nv-hig-list-item-context-cta.component';
import { NvHigListItemContextComponent } from './components/nv-hig-list-item/nv-hig-list-item-context/nv-hig-list-item-context.component';
import { NvHigListItemCoreComponent } from './components/nv-hig-list-item/nv-hig-list-item-core/nv-hig-list-item-core.component';
import { NvHigListItemLeadComponent } from './components/nv-hig-list-item/nv-hig-list-item-lead/nv-hig-list-item-lead.component';
import { NvHigListItemTrailComponent } from './components/nv-hig-list-item/nv-hig-list-item-trail/nv-hig-list-item-trail.component';
import { NvHigListItemComponent } from './components/nv-hig-list-item/nv-hig-list-item.component';
import { NvHigListMetadataComponent } from './components/nv-hig-list-metadata/nv-hig-list-metadata.component';
import { NvHigMarqueeComponent } from './components/nv-hig-marquee/nv-hig-marquee.component';
import { NvHigProgressCircularComponent } from './components/nv-hig-progress/nv-hig-progress-circular.component';
import { NvHigResourceProgressBarComponent } from './components/nv-hig-resource-progress-bar/nv-hig-resource-progress-bar.component';
import { NvHigRotaryComponent } from './components/nv-hig-rotary/nv-hig-rotary.component';
import { NvHigSidebarComponent } from './components/nv-hig-sidebar/nv-hig-sidebar.component';
import { NvHigSliderComponent } from './components/nv-hig-slider/nv-hig-slider.component';
import { NvHigTabsComponent } from './components/nv-hig-tabs/nv-hig-tabs.component';
import { NvHigThreeQuarterSheetComponent } from './components/nv-hig-three-quarter-sheet/nv-hig-three-quarter-sheet.component';
import { AutoFocusDirective } from './directives/auto-focus.directive';
import { BackToCloseModalDirective } from './directives/back-to-close-modal.directive';
import { CaptureRestoreFocusDirective } from './directives/capture-restore-focus.directive';
import { ClickableWhileScrollingDirective } from './directives/clickable-while-scrolling.directive';
import { EllipsifiedTextWithTooltipDirective } from './directives/ellipsified-text-with-tooltip.directive';
import { FastOneDimensionSpatialNavigationDirective } from './directives/fast-spatial-navigation/fast-one-dimenstion-spatial-navigation.directive';
import { FastSpatialNavigationItemDirective } from './directives/fast-spatial-navigation/fast-spatial-navigation-item.directive';
import { FocusBackDirective } from './directives/focus-back.directive';
import { NvFocusRippleDirective } from './directives/focus-ripple.directive';
import { HyperLinkUserActivationGuardDirective } from './directives/hyperlink-user-activation-guard.directive';
import { KnownSizesVirtualScroll } from './directives/known-sizes-virtual-scroll';
import { LongPressScrollerDirective } from './directives/long-press-scroller.directive';
import { NvDisableTooltipDirective } from './directives/nv-disable-tooltip.directive';
import { NvHigBackgroundImageOverlayDirective } from './directives/nv-hig-background-image-overlay.directive';
import {
    NvMatSelectKeyboardFixupDirective,
    NvMatSliderKeyboardFixupDirective,
    NvAddKeyboardActivateDirective,
    NvMatChipListKeyboardFixupDirective,
    NvMatMenuKeyboardFixupDirective,
    NvMatMenuFocusFixupDirective,
    NvMatExpansionPanelHeaderKeyboardFixupDirective,
    NvMatTabGroupFixupDirective,
    NvMapOptionNoSelectDirective
} from './directives/nv-mat-keyboard-fixups.directive';
import {
    CheckboxFocusMonitorDirective,
    NvMatRadioButtonSpatialNavigationFixupDirective,
    NvMatSidenavSpatialNavigationFixupDirective,
    NvMatMenuTriggerSpatialNavigationFixupDirective
} from './directives/nv-mat-spatial-navigation-fixups.directive';
import { OffDeviceLinkDirective } from './directives/off-device-link.directive';
import { NvProgressCircularDirective } from './directives/progress-circular.directive';
import {
    SpatialNavigationContainerDirective,
    SpatialNavigationGroupDirective
} from './directives/spatial-navigation-container.directive';
import { SpatialNavigationCustomSnapDirective } from './directives/spatial-navigation-custom-snap.directive';
import { SpatialNavigationFocusThenScrollDirective } from './directives/spatial-navigation-focus-then-scroll.directive';
import { SpatialNavigationScrollSnapDirective } from './directives/spatial-navigation-scroll-snap.directive';
import { SpatialNavigationVirtualScrollDirective } from './directives/spatial-navigation-virtual-scroll.directive';
import {
    SpatialNavRememberFocusDirective,
    SpatialNavForwardFocusDirective
} from './directives/SpatialNavMemory.directive';
import { TrapFocusDirective } from './directives/trap-focus.directive';
import { HeadingFocusableDirective } from './directives/heading-focusable.directive';
import { ScrollScrimDirective } from './directives/scroll-scrim.directive';
import { HigMaterialDesignModule } from './hig-material-design-module';

export function initializeIcons(reg: MatIconRegistry, san: DomSanitizer): Promise<any> {
    reg.addSvgIconSetLiteral(san.bypassSecurityTrustHtml(svgSprite));
    return Promise.resolve();
}

export function initializeIconFactory(reg: MatIconRegistry, san: DomSanitizer) {
    const x = () => initializeIcons(reg, san);
    return x;
}

export const iconFactoryProvider = {
    provide: APP_INITIALIZER,
    useFactory: initializeIconFactory,
    deps: [MatIconRegistry, DomSanitizer],
    multi: true
};

export const customTooltipDefaults: MatTooltipDefaultOptions = {
    showDelay: 0,
    hideDelay: 0,
    touchendHideDelay: 1500,
    disableTooltipInteractivity: false
};

@NgModule({
    exports: [
        NvHigSliderComponent,
        NvHigDialogComponent,
        NvHigBottomSheetComponent,
        NvHigThreeQuarterSheetComponent,
        NvHigProgressCircularComponent,
        NvHigMarqueeComponent,
        NvHigSidebarComponent,
        NvHigTabsComponent,
        NvHigCarouselComponent,
        NvHigListItemComponent,
        NvHigListItemLeadComponent,
        NvHigListItemCoreComponent,
        NvHigListItemTrailComponent,
        NvHigListItemContextComponent,
        NvHigListItemContextCtaComponent,
        NvHigRotaryComponent,
        NvHigFullPageDialogComponent,
        NvHigResourceProgressBarComponent,
        NvHigListInsertComponent,
        NvHigListMetadataComponent,
        NvHigBackgroundImageOverlayDirective,
        AutoFocusDirective,
        SpatialNavRememberFocusDirective,
        SpatialNavForwardFocusDirective,
        TrapFocusDirective,
        OffDeviceLinkDirective,
        FocusBackDirective,
        SpatialNavigationContainerDirective,
        SpatialNavigationGroupDirective,
        LongPressScrollerDirective,
        NvDisableTooltipDirective,
        NvFocusRippleDirective,
        NvMatSelectKeyboardFixupDirective,
        NvMatSliderKeyboardFixupDirective,
        NvAddKeyboardActivateDirective,
        NvMatChipListKeyboardFixupDirective,
        NvMatMenuKeyboardFixupDirective,
        NvMatMenuFocusFixupDirective,
        NvMatExpansionPanelHeaderKeyboardFixupDirective,
        NvMatTabGroupFixupDirective,
        NvMapOptionNoSelectDirective,
        BackToCloseModalDirective,
        CheckboxFocusMonitorDirective,
        NvMatRadioButtonSpatialNavigationFixupDirective,
        NvMatSidenavSpatialNavigationFixupDirective,
        NvMatMenuTriggerSpatialNavigationFixupDirective,
        SpatialNavigationVirtualScrollDirective,
        SpatialNavigationFocusThenScrollDirective,
        HyperLinkUserActivationGuardDirective,
        NvProgressCircularDirective,
        FastOneDimensionSpatialNavigationDirective,
        FastSpatialNavigationItemDirective,
        CaptureRestoreFocusDirective,
        EllipsifiedTextWithTooltipDirective,
        KnownSizesVirtualScroll,
        SpatialNavigationScrollSnapDirective,
        SpatialNavigationCustomSnapDirective,
        NvHigRotaryComponent,
        NvHigResourceProgressBarComponent,
        NvHigListInsertComponent,
        NvHigTableComparisonComponent,
        NvHigCellComparisonComponent,
        HeadingFocusableDirective,
        ScrollScrimDirective
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        CommonModule,
        FlexLayoutModule,
        HigMaterialDesignModule,
        TranslateModule,
        QRCodeComponent,
        MatDividerModule,
        RouterModule,
        MatMenuModule,
        ScrollingModule,
        NvHigTableComparisonComponent,
        NvHigCellComparisonComponent,
        NvHigDialogComponent,
        SpatialNavigationContainerDirective,
        SpatialNavigationGroupDirective,
        NvMatSelectKeyboardFixupDirective,
        NvMatSliderKeyboardFixupDirective,
        NvMatChipListKeyboardFixupDirective,
        NvMatExpansionPanelHeaderKeyboardFixupDirective,
        NvMatMenuKeyboardFixupDirective,
        NvMatMenuFocusFixupDirective,
        NvAddKeyboardActivateDirective,
        NvMatTabGroupFixupDirective,
        OffDeviceLinkDirective,
        TrapFocusDirective,
        AutoFocusDirective,
        AutoFocusDirective,
        SpatialNavRememberFocusDirective,
        SpatialNavForwardFocusDirective,
        FocusBackDirective,
        BackToCloseModalDirective,
        NvMatRadioButtonSpatialNavigationFixupDirective,
        CheckboxFocusMonitorDirective,
        NvDisableTooltipDirective,
        HyperLinkUserActivationGuardDirective,
        NvFocusRippleDirective,
        NvHigBackgroundImageOverlayDirective,
        LongPressScrollerDirective,
        NvMapOptionNoSelectDirective,
        NvMatSidenavSpatialNavigationFixupDirective,
        NvMatMenuTriggerSpatialNavigationFixupDirective,
        NvHigSliderComponent,
        NvHigBottomSheetComponent,
        NvHigThreeQuarterSheetComponent,
        NvHigProgressCircularComponent,
        NvHigMarqueeComponent,
        NvHigSidebarComponent,
        NvHigTabsComponent,
        NvHigCarouselComponent,
        NvHigListItemComponent,
        NvHigListItemLeadComponent,
        NvHigListItemCoreComponent,
        NvHigListItemTrailComponent,
        NvHigListItemContextComponent,
        NvHigListItemContextCtaComponent,
        NvHigRotaryComponent,
        SpatialNavigationVirtualScrollDirective,
        SpatialNavigationFocusThenScrollDirective,
        NvProgressCircularDirective,
        FastOneDimensionSpatialNavigationDirective,
        FastSpatialNavigationItemDirective,
        CaptureRestoreFocusDirective,
        EllipsifiedTextWithTooltipDirective,
        NvHigFullPageDialogComponent,
        KnownSizesVirtualScroll,
        SpatialNavigationScrollSnapDirective,
        SpatialNavigationCustomSnapDirective,
        NvHigResourceProgressBarComponent,
        NvHigListInsertComponent,
        NvHigListMetadataComponent,
        ClickableWhileScrollingDirective,
        HeadingFocusableDirective,
        ScrollScrimDirective
    ],
    providers: [
        iconFactoryProvider,
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                ...new MatDialogConfig(),
                restoreFocus: false
            } as MatDialogConfig
        },
        {
            provide: MAT_BOTTOM_SHEET_DEFAULT_OPTIONS,
            useValue: {
                ...new MatBottomSheetConfig(),
                restoreFocus: false
            } as MatBottomSheetConfig
        },
        {
            provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
            useValue: customTooltipDefaults
        },
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class HigModule {}
